// src/TelegramAuthPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Button, Divider, Stack } from '@mui/material';
import { FaTelegramPlane, FaCheckCircle, FaExclamationTriangle, FaExternalLinkAlt } from 'react-icons/fa';
import { verifyWidgetPayload, getBotStartLink, fetchTelegramStatus } from './services/telegramService';

export default function TelegramAuthPage() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const username = searchParams.get('username') || localStorage.getItem('kick_user') || 'Usuario';

  const [loading, setLoading] = useState(false);
  const [widgetScriptLoaded, setWidgetScriptLoaded] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const widgetRef = useRef(null);

  useEffect(() => {
    const container = widgetRef.current;
    if (!container) return;
    container.innerHTML = '';
    setWidgetScriptLoaded(false);

    let checkInterval = null;
    let safetyTimeout = null;

    window.onTelegramAuthCallback = async (telegramData) => {
      setLoading(true);
      setError(null);
      try {
        const res = await verifyWidgetPayload(telegramData, username);
        if (res && res.success) {
          // Consultar el estado real en backend para asegurar que se verificó y no pertenece a otra cuenta
          const statusRes = await fetchTelegramStatus(username, telegramData?.id);
          if (statusRes && (statusRes.verified || statusRes.isVerified)) {
            if (statusRes.username && username && statusRes.username.toLowerCase() !== username.toLowerCase()) {
              setError(`Esta cuenta de Telegram ya se encuentra asociada a @${statusRes.username}.`);
            } else {
              setSuccess(true);
            }
          } else {
            setError(statusRes?.error || statusRes?.message || 'No se pudo confirmar la vinculación. El ID de Telegram podría estar ya asociado a otra cuenta.');
          }
        } else {
          setError(res?.error || res?.message || 'No se pudo verificar la cuenta.');
        }
      } catch (err) {
        setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Error de conexión.');
      } finally {
        setLoading(false);
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', 'dng2bot');
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuthCallback(user)');
    script.setAttribute('data-request-access', 'write');

    script.onload = () => {
      let checks = 0;
      checkInterval = setInterval(() => {
        checks++;
        const iframe = container.querySelector('iframe');
        if (iframe || checks > 20) {
          setWidgetScriptLoaded(true);
          if (checkInterval) clearInterval(checkInterval);
        }
      }, 150);
    };

    script.onerror = () => {
      setWidgetScriptLoaded(true);
    };

    container.appendChild(script);

    safetyTimeout = setTimeout(() => {
      setWidgetScriptLoaded(true);
    }, 3000);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (safetyTimeout) clearTimeout(safetyTimeout);
      delete window.onTelegramAuthCallback;
    };
  }, [username]);

  const handleOpenBot = () => {
    const url = getBotStartLink(username);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0b0e14',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 440,
          width: '100%',
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          backgroundColor: 'rgba(15, 20, 32, 0.95)',
          border: '1px solid rgba(0, 136, 204, 0.3)',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 136, 204, 0.2)',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
            boxShadow: '0 4px 15px rgba(0, 136, 204, 0.4)',
          }}
        >
          <FaTelegramPlane size={28} color="#fff" />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, fontFamily: 'Outfit, sans-serif' }}>
          Verificación Telegram
        </Typography>

        <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 2 }}>
          Conectá tu cuenta oficial de Telegram para validar tu usuario en D-SIDE.
        </Typography>

        {username && (
          <Box
            sx={{
              display: 'inline-block',
              bgcolor: 'rgba(0, 136, 204, 0.15)',
              border: '1px solid rgba(0, 136, 204, 0.4)',
              borderRadius: 3,
              px: 2,
              py: 0.5,
              color: '#0088cc',
              fontWeight: 700,
              fontSize: '0.85rem',
              mb: 3,
            }}
          >
            @{username}
          </Box>
        )}

        {success ? (
          <Box sx={{ py: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'rgba(83, 252, 24, 0.15)',
                color: '#53FC18',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <FaCheckCircle size={36} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#53FC18', mb: 1 }}>
              ¡Verificación Exitosa!
            </Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3 }}>
              Tu cuenta de Telegram ha sido vinculada correctamente. Ya podés volver a la app.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                try {
                  window.close();
                } catch (e) { }
              }}
              sx={{
                py: 1.2,
                borderRadius: 3,
                bgcolor: '#0088cc',
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Volver a la App
            </Button>
          </Box>
        ) : (
          <Box>
            {error && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#EF4444',
                  mb: 2.5,
                  textAlign: 'left',
                }}
              >
                <FaExclamationTriangle size={18} style={{ flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#FCA5A5', fontWeight: 600 }}>
                  {error}
                </Typography>
              </Box>
            )}

            {/* Opción 1: Bot de Telegram (Mobile Direct) */}
            {/* <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(0, 136, 204, 0.15) 0%, rgba(34, 158, 217, 0.08) 100%)',
                border: '1px solid rgba(0, 136, 204, 0.4)',
                mb: 2,
                textAlign: 'left'
              }}
            > */}
            {/* <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFF', mb: 0.5 }}>
                ⚡ Abrir en App de Telegram (Recomendado)
              </Typography>
              <Typography variant="caption" sx={{ color: '#D1D5DB', display: 'block', mb: 1.5, lineHeight: 1.3 }}>
                Iniciá el bot y compartí tu celular para verificar tu cuenta al instante.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={handleOpenBot}
                startIcon={<FaTelegramPlane />}
                endIcon={<FaExternalLinkAlt size={12} />}
                sx={{
                  py: 1,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.9rem'
                }}
              >
                Abrir Bot de Telegram
              </Button>
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'center', my: 2, gap: 1.5 }}>
              <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                O con el botón web
              </Typography>
              <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            </Box> */}

            {/* Opción 2: Widget con loader */}
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(0, 136, 204, 0.05)',
                border: '1px solid rgba(0, 136, 204, 0.2)',
                minHeight: 70,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={28} sx={{ color: '#53FC18' }} />
                  <Typography variant="caption" sx={{ color: '#53FC18' }}>
                    Verificando cuenta...
                  </Typography>
                </Box>
              ) : (
                <>
                  {!widgetScriptLoaded && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={24} sx={{ color: '#0088cc' }} />
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        Cargando botón de Telegram...
                      </Typography>
                    </Box>
                  )}
                  <Box
                    ref={widgetRef}
                    sx={{
                      display: widgetScriptLoaded ? 'flex' : 'none',
                      justifyContent: 'center',
                      minHeight: 44,
                      width: '100%'
                    }}
                  />
                </>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

